import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});
const LAST_WEB_ROUTE_KEY = 'bereket_last_web_route';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionChecked, setSessionChecked] = useState(false);

  const clearStoredAuthState = useCallback(() => {
    localStorage.removeItem(LAST_WEB_ROUTE_KEY);
    localStorage.removeItem('bereket-auth');
    localStorage.removeItem('bereket_session');
    sessionStorage.removeItem('bereket-auth');
    sessionStorage.removeItem('bereket_session');

    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('sb-') && key.includes('-auth-token')) {
        localStorage.removeItem(key);
      }
    });

    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith('sb-') && key.includes('-auth-token')) {
        sessionStorage.removeItem(key);
      }
    });
  }, []);

  const extractUserData = (session) => {
    if (!session?.user) return null;
    return {
      ...session.user,
      name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User'
    };
  };

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
        }

        if (mounted) {
          if (session?.user) {
            setUser(extractUserData(session));
          } else {
            setUser(null);
          }
          setLoading(false);
          setSessionChecked(true);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted) {
          setUser(null);
          setLoading(false);
          setSessionChecked(true);
        }
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;

        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
          if (session) {
            setUser(extractUserData(session));
          }
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
        } else if (event === 'INITIAL_SESSION') {
          if (session) {
            setUser(extractUserData(session));
          }
          setLoading(false);
          setSessionChecked(true);
        }

        // Always ensure loading is resolved after any auth event
        if (event !== 'INITIAL_SESSION') {
          setLoading(false);
          setSessionChecked(true);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Refresh session when tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        try {
          // Use refreshSession to get a fresh token, not just cached session
          const { data: { session }, error } = await supabase.auth.refreshSession();
          if (error) {
            // If refresh fails, try getSession as fallback (might still be valid)
            const { data: { session: cached } } = await supabase.auth.getSession();
            if (cached?.user) {
              setUser(extractUserData(cached));
            } else {
              setUser(null);
            }
          } else if (session?.user) {
            setUser(extractUserData(session));
          }
        } catch (e) {
          console.error('Visibility refresh error:', e);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Proactive token refresh every 10 minutes to prevent silent expiry
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          // Check if token expires within 5 minutes
          const expiresAt = session.expires_at;
          const now = Math.floor(Date.now() / 1000);
          if (expiresAt && expiresAt - now < 300) {
            const { data: { session: refreshed } } = await supabase.auth.refreshSession();
            if (refreshed?.user) {
              setUser(extractUserData(refreshed));
            }
          }
        }
      } catch (e) {
        // Silent - autoRefreshToken handles most cases
      }
    }, 600000); // 10 minutes
    return () => clearInterval(interval);
  }, []);

  const signUp = async (email, password, name) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name }
      }
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) {
      // Clean up common Supabase error messages
      const msg = error.message || 'Login failed';
      if (msg.includes('body stream already read') || msg.includes('Invalid login')) {
        throw new Error('Pogrešan email ili lozinka');
      }
      throw error;
    }
    return data;
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error && !String(error.message || '').toLowerCase().includes('session')) {
        throw error;
      }
    } finally {
      clearStoredAuthState();
      setUser(null);
    }
  };

  const refreshSession = useCallback(async () => {
    try {
      const { data: { session }, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      if (session) {
        setUser(extractUserData(session));
        return true;
      }
    } catch (error) {
      console.error('Error refreshing session:', error);
    }
    return false;
  }, []);

  const value = {
    user,
    loading,
    sessionChecked,
    signUp,
    signIn,
    signOut,
    refreshSession
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
